import React, { useEffect, useRef } from 'react';

/** One persistent scene. Frames run only while easing toward new input. */
export function ImmersiveBackground({ disabled }: { disabled: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scene = ref.current;
    if (!scene) return;
    const root = scene.parentElement;
    const glow = glowRef.current;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let frame = 0;
    let lastTime = 0;
    let targetX = 0;
    let targetY = 0;
    let targetScroll = 0;
    let x = 0;
    let y = 0;
    let scroll = 0;
    const clamp = (n: number) => Math.max(0, Math.min(1, n));
    const render = () => {
      scene.style.setProperty('--scene-x', `${x * 24}px`);
      scene.style.setProperty('--scene-y', `${y * 18}px`);
      scene.style.setProperty('--scene-scroll', String(scroll));
      scene.style.setProperty('--scene-scale', `${1.06 + scroll * .48}`);
      scene.style.setProperty('--scene-light-x', `${50 + x * 24}%`);
      scene.style.setProperty('--scene-light-y', `${46 + y * 20}%`);
      root?.style.setProperty('--pointer-drift-x', `${x * 7}px`);
      root?.style.setProperty('--pointer-drift-y', `${y * 5}px`);
      if (glow) {
        glow.style.transform = `translate3d(${(x + 1) * innerWidth / 2}px, ${(y + 1) * innerHeight / 2}px, 0) translate(-50%, -50%)`;
      }
    };
    const tick = (time: number) => {
      frame = 0;
      const delta = lastTime ? Math.min(time - lastTime, 64) : 16;
      lastTime = time;
      const ease = 1 - Math.exp(-delta / 110);
      x += (targetX - x) * ease;
      y += (targetY - y) * ease;
      scroll += (targetScroll - scroll) * ease;
      const moving = Math.abs(targetX - x) + Math.abs(targetY - y) + Math.abs(targetScroll - scroll) > .001;
      if (!moving) { x = targetX; y = targetY; scroll = targetScroll; }
      render();
      if (moving) frame = requestAnimationFrame(tick);
      else lastTime = 0;
    };
    const schedule = () => {
      if (!disabled && !document.hidden && !frame) frame = requestAnimationFrame(tick);
    };
    const onScroll = () => {
      targetScroll = clamp(window.scrollY / Math.max(1, document.documentElement.scrollHeight - innerHeight));
      schedule();
    };
    const onPointer = (event: PointerEvent) => {
      if (!finePointer.matches || event.pointerType !== 'mouse') return;
      targetX = clamp(event.clientX / innerWidth) * 2 - 1;
      targetY = clamp(event.clientY / innerHeight) * 2 - 1;
      if (glow) glow.dataset.active = 'true';
      schedule();
    };
    const resetPointer = () => {
      if (glow) glow.dataset.active = 'false';
      targetX = 0; targetY = 0; schedule();
    };
    const visibility = () => {
      if (document.hidden) { resetPointer(); cancelAnimationFrame(frame); frame = 0; lastTime = 0; }
      else onScroll();
    };
    render();
    if (glow) glow.dataset.active = 'false';
    if (disabled) return;
    onScroll();
    window.addEventListener('pointermove', onPointer, { passive: true });
    document.documentElement.addEventListener('pointerleave', resetPointer);
    window.addEventListener('blur', resetPointer);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('visibilitychange', visibility);
    finePointer.addEventListener('change', resetPointer);
    const resize = new ResizeObserver(onScroll);
    resize.observe(root ?? document.body);
    return () => {
      if (glow) glow.dataset.active = 'false';
      cancelAnimationFrame(frame); resize.disconnect();
      window.removeEventListener('pointermove', onPointer);
      document.documentElement.removeEventListener('pointerleave', resetPointer);
      window.removeEventListener('blur', resetPointer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', visibility);
      finePointer.removeEventListener('change', resetPointer);
    };
  }, [disabled]);

  return <><div ref={ref} className="immersive-background" aria-hidden="true">
    <div className="immersive-background__image"><img src="/cognixis-hero.png" alt="" decoding="async" loading="eager" /></div>
    <div className="immersive-background__shade" />
    <div className="immersive-background__light" />
  </div><div ref={glowRef} className="cursor-highlight" aria-hidden="true" /></>;
}
